import { useCallback } from 'react';
import { useEditor } from '@craftjs/core';
import { useAppDispatch, useAppStore } from '@/store/hooks';
import { setTranslate } from '@/store/zoom/panZoomSlice';
import { getBoundaryDomElement } from '@/utils/helperFunc';

const SCROLL_PADDING = 60;
const TRANSITION_TIMEOUT_MS = 350;

export const useScrollNodeIntoView = (): ((elementId: string, center?: boolean) => void) => {
  const { query } = useEditor();
  const dispatch = useAppDispatch();
  const store = useAppStore();

  return useCallback(
    (elementId: string, center = false) => {
      requestAnimationFrame(() => {
        const elementDom = query.node(elementId).get()?.dom;
        if (!elementDom) return;

        const measureTarget = getBoundaryDomElement(elementDom);

        const pageContainer = document.getElementById('page-container');
        const renderer = pageContainer?.querySelector<HTMLElement>('.craftjs-renderer');
        if (!pageContainer || !renderer) return;

        const elementRect = measureTarget.getBoundingClientRect();
        const containerRect = pageContainer.getBoundingClientRect();

        let deltaX = 0;
        let deltaY = 0;

        if (center) {
          const containerCenterX = (containerRect.left + containerRect.right) / 2;
          const containerCenterY = (containerRect.top + containerRect.bottom) / 2;
          const elementCenterX = (elementRect.left + elementRect.right) / 2;
          const elementCenterY = (elementRect.top + elementRect.bottom) / 2;

          deltaX = containerCenterX - elementCenterX;
          deltaY = containerCenterY - elementCenterY;
        } else {
          const padding = SCROLL_PADDING;

          if (elementRect.top < containerRect.top + padding) {
            deltaY = containerRect.top + padding - elementRect.top;
          } else if (elementRect.bottom > containerRect.bottom - padding) {
            deltaY = containerRect.bottom - padding - elementRect.bottom;
          }

          if (elementRect.left < containerRect.left + padding) {
            deltaX = containerRect.left + padding - elementRect.left;
          } else if (elementRect.right > containerRect.right - padding) {
            deltaX = containerRect.right - padding - elementRect.right;
          }
        }

        if (deltaX === 0 && deltaY === 0) return;

        const { translateX, translateY } = store.getState().panZoom;
        const targetX = translateX + deltaX;
        const targetY = translateY + deltaY;

        renderer.style.transition = 'transform 300ms ease-out';
        dispatch(setTranslate({ x: targetX, y: targetY }));

        const cleanup = () => {
          renderer.style.transition = '';
          renderer.removeEventListener('transitionend', cleanup);
          // Re-dispatch with a sub-pixel nudge to force RenderNode to
          // recalculate control positions now that the transition is complete.
          dispatch(setTranslate({ x: targetX + 0.001, y: targetY }));
        };
        renderer.addEventListener('transitionend', cleanup);
        setTimeout(cleanup, TRANSITION_TIMEOUT_MS);
      });
    },
    [query, dispatch, store],
  );
};
